import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { RootCompanyDocument } from 'Main/SecuredRoutes/graphql/rootCompany.gql';

import useCompanyContext from 'common/hooks/useCompanyContext';
import useSessionStorage from 'common/hooks/useSessionStorage';

import { REASONS_KEY } from '../../../constants';
import { AccountClosureFormValues } from '../../../types';
import { getFormattedAccountClosureFormValues } from '../../helpers';
import { CreateAccountClosureDocument } from './createAccountClosure.gql';

const useCreateAccountClosure = () => {
  const { companyProfileId } = useCompanyContext();

  const [mutateFunction, { loading }] = useMutation(
    CreateAccountClosureDocument,
    {
      awaitRefetchQueries: true,
      refetchQueries: () => [
        {
          query: RootCompanyDocument,
          variables: { companyProfileId },
        },
      ],
    },
  );

  const { getValues } = useFormContext();

  const accountClosureFormValues = getValues() as AccountClosureFormValues;
  const formattedAccountClosureFormValues =
    getFormattedAccountClosureFormValues(accountClosureFormValues);

  const [, , removeReasons] = useSessionStorage(REASONS_KEY);

  const createAccountClosure = useCallback(
    async (token: string) => {
      await mutateFunction({
        context: {
          headers: {
            'short-lived-token': token,
          },
        },
        variables: {
          input: {
            companyProfileId: companyProfileId as string,
            ...formattedAccountClosureFormValues,
          },
        },
      });

      // once the account closure has completed
      // we should remove the reason from the session
      removeReasons();
    },
    [
      companyProfileId,
      formattedAccountClosureFormValues,
      mutateFunction,
      removeReasons,
    ],
  );

  const canCloseAccount = accountClosureFormValues.reasons.size > 0;

  return {
    canCloseAccount,
    createAccountClosure,
    loading,
  };
};

export default useCreateAccountClosure;
